import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
type ResizeDragHandlers = {
  // Per-drag setup, e.g. capturing the start position or measuring the column.
  onStart?: (event: React.PointerEvent<HTMLElement>) => void;
  onMove: (event: PointerEvent) => void;
  // Fires once when the gesture ends, on pointerup or pointercancel (a cancelled gesture is
  // the lost-pointer-capture case). Commit the resize here.
  onEnd: () => void;
};

// Shared pointer-drag lifecycle for the block-width and chart-height resize handles: capture the
// pointer, forward moves, and fire onEnd exactly once on release or cancel. Callers own the axis
// math; this owns only listener setup/teardown and the isResizing flag. Listeners live on the
// handle (event.currentTarget) so the pointer capture keeps tracking outside the element.
export const useResizeDrag = t0 => {
  const $ = _c(9);
  const {
    onStart,
    onMove,
    onEnd
  } = t0;
  const [isResizing, setIsResizing] = useState(false);
  const teardownRef = useRef(null);
  let t1;
  if ($[0] !== onEnd || $[1] !== onMove || $[2] !== onStart) {
    t1 = event => {
      event.preventDefault();
      const target = event.currentTarget;
      target.setPointerCapture?.(event.pointerId);
      setIsResizing(true);
      onStart?.(event);
      const detachedWatcher = new MutationObserver(() => {
        if (!target.isConnected) {
          abandon();
        }
      });
      const detach = () => {
        detachedWatcher.disconnect();
        target.removeEventListener("pointermove", forwardMove);
        target.removeEventListener("pointerup", end);
        target.removeEventListener("pointercancel", end);
        teardownRef.current = null;
      };
      const abandon = () => {
        detach();
        setIsResizing(false);
      };
      const forwardMove = move => {
        if (!target.isConnected) {
          return abandon();
        }
        onMove(move);
      };
      const end = () => {
        if (!target.isConnected) {
          return abandon();
        }
        detach();
        setIsResizing(false);
        onEnd();
      };
      teardownRef.current = detach;
      if (target.parentNode) {
        detachedWatcher.observe(target.parentNode, {
          childList: true
        });
      }
      target.addEventListener("pointermove", forwardMove);
      target.addEventListener("pointerup", end);
      target.addEventListener("pointercancel", end);
    };
    $[0] = onEnd;
    $[1] = onMove;
    $[2] = onStart;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onPointerDown = t1;
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => teardownRef.current?.();
    t3 = [];
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== isResizing || $[7] !== onPointerDown) {
    t4 = {
      onPointerDown,
      isResizing
    };
    $[6] = isResizing;
    $[7] = onPointerDown;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};